// Pure helpers for a coach's personal invite code, client link, plan wording and
// share message (My link, Team, admin Plans). The API owns validation; these mirror
// it so the form can explain problems before saving.

export const INVITE_CODE_RE = /^[A-Z0-9-]{3,20}$/;
export const INVITE_CODE_MAX = 20;
export const WELCOME_MAX = 1000;
export const NAME_MAX = 80;

/** What the code field shows while typing: capitals, spaces become dashes, nothing else allowed. */
export function normalizeInviteCodeInput(raw) {
  return String(raw ?? '')
    .toUpperCase()
    .replace(/\s+/g, '-')
    .replace(/[^A-Z0-9-]/g, '')
    .slice(0, INVITE_CODE_MAX);
}

/** null when the code is valid, otherwise 'required' | 'tooShort' | 'invalid'. */
export function inviteCodeError(code) {
  const value = String(code ?? '').trim();
  if (!value) return 'required';
  if (value.length < 3) return 'tooShort';
  if (!INVITE_CODE_RE.test(value)) return 'invalid';
  return null;
}

/** null when empty or https, otherwise 'https'. */
export function photoUrlError(url) {
  const value = String(url ?? '').trim();
  if (!value) return null;
  return /^https:\/\/\S+$/.test(value) ? null : 'https';
}

export function formatDollars(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

/** The trial in words: "1 free month, no card" / "7-day trial with card" / "No trial". */
export function trialWording(preset, language = 'english') {
  const he = language === 'hebrew';
  const type = preset?.trial_type;
  if (type === 'free_month') return he ? 'חודש ראשון חינם, בלי כרטיס אשראי' : '1 free month, no card';
  if (type === 'card_trial') {
    const days = Number(preset?.trial_days) || 0;
    if (days > 0) return he ? `ניסיון של ${days} ימים עם כרטיס אשראי` : `${days}-day trial with card`;
  }
  return he ? 'ללא תקופת ניסיון' : 'No trial';
}

/** "$15 / month · $120 / year" */
export function priceWording(preset, language = 'english') {
  const he = language === 'hebrew';
  const parts = [];
  if (preset?.monthly_dollars != null) parts.push(`${formatDollars(preset.monthly_dollars)} ${he ? 'לחודש' : '/ month'}`);
  if (preset?.yearly_dollars != null) parts.push(`${formatDollars(preset.yearly_dollars)} ${he ? 'לשנה' : '/ year'}`);
  return parts.join(' · ');
}

export function presetName(preset, language = 'english') {
  if (!preset) return '';
  return (language === 'hebrew' ? preset.name_he : preset.name_en) || preset.name_en || preset.name_he || preset.id || '';
}

/** Bilingual share text, the sender's language first. */
export function buildWhatsAppMessage({ link, code, language = 'english' }) {
  const en = [
    `Hi! Start your BetterChoice plan with me: ${link}`,
    code ? `If the app asks for a code, mine is ${code}.` : null,
  ].filter(Boolean).join('\n');
  const he = [
    `היי! מתחילים איתי את התוכנית ב-BetterChoice: ${link}`,
    code ? `אם האפליקציה מבקשת קוד, הקוד שלי הוא ${code}.` : null,
  ].filter(Boolean).join('\n');
  return language === 'hebrew' ? `${he}\n\n${en}` : `${en}\n\n${he}`;
}

export function whatsAppShareUrl(message) {
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

/** Link a staff invitee opens to sign up on this site. */
export function staffSignupLink(origin, inviteCode) {
  const base = String(origin || '').replace(/\/+$/, '');
  return `${base}/signup?invite=${encodeURIComponent(inviteCode)}`;
}

export function initialsFrom(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** A plan id from its English name: "Starter 15" -> "starter_15". */
export function presetIdFromName(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

/**
 * First problem in the extra questions, or null.
 * Returns { index, reason: 'question' | 'options' }.
 */
export function customStepsError(steps) {
  const list = Array.isArray(steps) ? steps : [];
  for (let i = 0; i < list.length; i += 1) {
    const step = list[i] || {};
    if (!String(step.question || '').trim()) return { index: i, reason: 'question' };
    if (step.type !== 'text') {
      const options = (Array.isArray(step.options) ? step.options : []).filter((o) => String(o).trim());
      if (options.length === 0) return { index: i, reason: 'options' };
    }
  }
  return null;
}

const EDITABLE_FIELDS = ['invite_code', 'display_name', 'title', 'photo_url', 'welcome_message', 'pricing_preset_id'];

const emptyToNull = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
};

/** The form state for a settings object from the API. */
export function settingsToForm(settings, suggestedCode) {
  const s = settings || {};
  return {
    invite_code: s.invite_code || suggestedCode || '',
    display_name: s.display_name || '',
    title: s.title || '',
    photo_url: s.photo_url || '',
    welcome_message: s.welcome_message || '',
    pricing_preset_id: s.pricing_preset_id || null,
    custom_steps: (Array.isArray(s.custom_steps) ? s.custom_steps : []).map((step) => ({
      id: step.id,
      type: step.type || 'text',
      question: step.question || '',
      description: step.description || '',
      options: Array.isArray(step.options) ? step.options : [],
    })),
  };
}

function stepsForApi(steps) {
  return (steps || []).map((step) => ({
    ...(step.id ? { id: step.id } : {}),
    type: step.type || 'text',
    question: String(step.question || '').trim(),
    ...(String(step.description || '').trim() ? { description: String(step.description).trim() } : {}),
    ...(step.type !== 'text' ? { options: (step.options || []).map((o) => String(o).trim()).filter(Boolean) } : {}),
  }));
}

/** Only the fields that differ from what the API returned (empty text becomes null). */
export function settingsPatch(original, form) {
  const before = original || {};
  const patch = {};
  for (const key of EDITABLE_FIELDS) {
    const next = key === 'invite_code' ? emptyToNull(normalizeInviteCodeInput(form[key])) : emptyToNull(form[key]);
    if (next !== emptyToNull(before[key])) patch[key] = next;
  }
  const nextSteps = stepsForApi(form.custom_steps);
  const prevSteps = stepsForApi(settingsToForm(before).custom_steps);
  if (JSON.stringify(nextSteps) !== JSON.stringify(prevSteps)) patch.custom_steps = nextSteps;
  return patch;
}
